/**
 * Feature Flag Provider - Dual Architecture Implementation
 *
 * This file implements a dual-provider architecture to support feature flag functionality
 * across both authenticated and unauthenticated application states. This refactoring was
 * necessary to resolve dependency conflicts that prevented design system functionality
 * from working on the login screen.
 *
 * Architecture Overview:
 * 1. UnauthenticatedFeatureFlagProvider - Lightweight provider for login screen
 * 2. AuthenticatedFeatureFlagProviderImpl - Full-featured provider for authenticated users
 * 3. Main FeatureFlagProvider - Routes to appropriate implementation
 *
 * Problem Context:
 * The original implementation required UserProvider and AuthSessionProvider contexts,
 * which are only available after authentication. This created a circular dependency:
 * - ThemeEffectManager needed feature flags to work on login screen
 * - Feature flags needed user context (unavailable on login screen)
 * - Design system toggle (Ctrl+I) was non-functional before authentication
 *
 * Solution:
 * Split implementation into two distinct providers that can operate independently,
 * ensuring feature flag functionality is available throughout the application lifecycle.
 */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { FeatureFlag, UserGroup } from "~/api/API";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { useCurrentUser } from "~/auth/UserContext";
import { FeatureFlagEvaluationContext } from "./evaluate";
import { FEATURE_FLAGS, featureFlagService } from "./service";

interface FeatureFlagContextValue {
  flags: Map<string, FeatureFlag>;
  isFeatureEnabled: (
    key: string | FEATURE_FLAGS,
    additionalContext?: { doctorId?: string },
  ) => Promise<boolean>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

/**
 * Unauthenticated Feature Flag Provider
 *
 * Purpose: Provides basic feature flag functionality for unauthenticated users (login screen)
 *
 * Why needed:
 * - Enables design system toggle on login screen without requiring user context
 * - Prevents dependency on UserProvider/AuthSessionProvider which are unavailable pre-auth
 * - Allows ThemeEffectManager and GlobalKeyboardShortcuts to function on login
 *
 * Implementation details:
 * - No API calls or user-specific evaluation
 * - Minimal resource footprint
 * - Always ready state (loading: false)
 */
function UnauthenticatedFeatureFlagProvider({
  children,
}: FeatureFlagProviderProps) {
  const isFeatureEnabled = useCallback(
    (key: string | FEATURE_FLAGS): Promise<boolean> => {
      // Return default values for known flags without user context
      if (key === FEATURE_FLAGS.EXAMPLE_FEATURE) {
        return Promise.resolve(true);
      }

      return Promise.resolve(false); // Other flags default to disabled
    },
    [],
  );

  const value: FeatureFlagContextValue = {
    flags: new Map(),
    isFeatureEnabled,
    loading: false,
    error: null,
    refresh: async () => {
      /* no-op */
    },
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Authenticated Feature Flag Provider Implementation
 *
 * Purpose: Full-featured provider for authenticated users with complete API integration
 *
 * Why needed:
 * - Provides complete feature flag functionality after user authentication
 * - Integrates with backend feature flag service for real-time evaluation
 * - Supports user-specific and role-based flag evaluation
 * - Maintains flag cache for performance optimization
 *
 * Key features:
 * - Real-time flag fetching from backend API
 * - User context integration (user ID, role, additional context)
 * - Caching mechanism with Map data structure for O(1) lookups
 * - Error handling and loading states
 * - Automatic refresh capability
 *
 * Hook dependencies:
 * - useCurrentUser: Provides user context for personalized flag evaluation
 * - useAuthSession: Provides user role/group information
 *
 * Safe usage: This component is only rendered after authentication, ensuring
 * all required contexts (UserProvider, AuthSessionProvider) are available.
 */
function AuthenticatedFeatureFlagProviderImpl({
  children,
}: FeatureFlagProviderProps) {
  const [flags, setFlags] = useState<Map<string, FeatureFlag>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // These hooks are safe to use here as this component is only rendered after authentication
  const { user } = useCurrentUser();
  const { userGroup } = useAuthSession();

  const isFeatureEnabled = useCallback(
    async (
      key: string | FEATURE_FLAGS,
      additionalContext?: { doctorId?: string },
    ): Promise<boolean> => {
      const context: FeatureFlagEvaluationContext = {
        userId: user?.id,
        userRole: mapUserGroup(userGroup),
        ...additionalContext,
      };
      return featureFlagService.isFeatureEnabled(key, context);
    },
    [user?.id, userGroup],
  );

  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const allFlags = await featureFlagService.getAllFeatureFlags();

      // Convert to Map for O(1) lookups
      const flagMap = new Map<string, FeatureFlag>();
      allFlags.forEach((flag) => {
        if (flag.key) {
          flagMap.set(flag.key, flag);
        }
      });

      setFlags(flagMap);
    } catch (err) {
      console.error("Failed to fetch feature flags:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch flags on mount and when user changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    void fetchFlags();
  }, [fetchFlags, user?.id, userGroup]);

  const value: FeatureFlagContextValue = {
    flags,
    isFeatureEnabled,
    loading,
    error,
    refresh: fetchFlags,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

// Main provider that determines which implementation to use
export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  // Use simple provider for login screen and before authentication
  // This prevents dependency on UserProvider and AuthSessionProvider
  return (
    <UnauthenticatedFeatureFlagProvider>
      {children}
    </UnauthenticatedFeatureFlagProvider>
  );
}

// Export authenticated version for use after login
export function AuthenticatedFeatureFlagProvider({
  children,
}: FeatureFlagProviderProps) {
  return (
    <AuthenticatedFeatureFlagProviderImpl>
      {children}
    </AuthenticatedFeatureFlagProviderImpl>
  );
}

// Hook to access feature flag context
export function useFeatureFlags(): FeatureFlagContextValue {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider",
    );
  }
  return context;
}

// Simple hook to check a single feature flag
export function useFeatureFlag(
  key: string | FEATURE_FLAGS,
  additionalContext?: { doctorId?: string },
): {
  isEnabled: boolean;
  loading: boolean;
  error: string | null;
} {
  const {
    isFeatureEnabled,
    loading: contextLoading,
    error,
  } = useFeatureFlags();
  const [isEnabled, setIsEnabled] = useState(false);
  const [flagLoading, setFlagLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const checkFlag = async () => {
      // Don't set loading if context is still loading
      if (contextLoading) {
        return;
      }

      try {
        const enabled = await isFeatureEnabled(key, additionalContext);
        if (mounted) {
          setIsEnabled(enabled);
          setFlagLoading(false);
        }
      } catch (err) {
        console.error(`Failed to check feature flag "${key}":`, err);
        if (mounted) {
          setIsEnabled(false);
          setFlagLoading(false);
        }
      }
    };

    void checkFlag();

    return () => {
      mounted = false;
    };
  }, [isFeatureEnabled, key, additionalContext, contextLoading]);

  return {
    isEnabled,
    loading: contextLoading || flagLoading,
    error,
  };
}

// Component wrapper for conditional rendering based on feature flags
interface FeatureGateProps {
  flag: string | FEATURE_FLAGS;
  children: ReactNode;
  fallback?: ReactNode;
  loading?: ReactNode;
  doctorId?: string;
}

export function FeatureGate({
  flag,
  children,
  fallback = null,
  loading: loadingComponent = null,
  doctorId,
}: FeatureGateProps): JSX.Element | null {
  const { isEnabled, loading } = useFeatureFlag(flag, { doctorId });

  if (loading) {
    return loadingComponent !== null ? <>{loadingComponent}</> : null;
  }

  if (isEnabled) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// ************** Private functions **************

// Map AuthUserGroup to UserGroup for GraphQL compatibility
function mapUserGroup(authGroup: string | null): UserGroup | undefined {
  switch (authGroup) {
    case "Admin":
      return UserGroup.ADMIN;
    case "Doctor":
      return UserGroup.DOCTOR;
    case "MedicalAssistant":
      return UserGroup.MEDICAL_ASSISTANT;
    case "Historian":
      return UserGroup.HISTORIAN;
    default:
      return UserGroup.UNAUTHORIZED;
  }
}
